import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CaseStudyCarousel = () => {
  const [activeCase, setActiveCase] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const caseStudies = [
    {
      id: 'fintech-transformation',
      company: 'Global FinTech Platform',
      industry: 'Financial Technology',
      challenge: 'Legacy monolith preventing rapid feature delivery',
      solution: 'Microservices architecture with ownership-driven teams',
      timeline: '6 months',
      teamSize: '120+ engineers',
      beforeMetrics: {
        deployments: '2 per month',
        incidents: '15 per month',
        velocity: '30% capacity',
        satisfaction: '45% team satisfaction'
      },
      afterMetrics: {
        deployments: '50+ per week',
        incidents: '2 per month',
        velocity: '85% capacity',
        satisfaction: '92% team satisfaction'
      },
      keyResults: [
        '25x increase in deployment frequency',
        '87% reduction in production incidents',
        '180% improvement in feature delivery velocity',
        'Complete cultural transformation to ownership model'
      ],
      testimonial: `SIGNA didn't just redesign our architecture—they transformed how our teams think about ownership and delivery. The results speak for themselves.`,
      author: 'Sarah Chen, VP of Engineering',image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&h=600&fit=crop'
    },
    {
      id: 'ecommerce-scale',company: 'Enterprise E-commerce',industry: 'Retail Technology',challenge: 'Platform couldn\'t handle peak traffic loads',
      solution: 'Cloud-native architecture with auto-scaling capabilities',
      timeline: '4 months',
      teamSize: '80+ engineers',
      beforeMetrics: {
        uptime: '94% during peaks',
        scaling: 'Manual intervention',
        performance: '8s page load',
        costs: '$2M annual infrastructure'
      },
      afterMetrics: {
        uptime: '99.9% during peaks',
        scaling: 'Automatic scaling',
        performance: '1.2s page load',
        costs: '$800K annual infrastructure'
      },
      keyResults: [
        '99.9% uptime during Black Friday peak',
        '85% reduction in page load times',
        '60% reduction in infrastructure costs',
        'Zero manual scaling interventions required'
      ],
      testimonial: `The platform now handles 10x our previous peak traffic without breaking a sweat. Our customers notice the difference.`,
      author: 'Michael Rodriguez, CTO',
      image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=600&fit=crop'
    },
    {
      id: 'healthcare-compliance',
      company: 'Healthcare Technology',
      industry: 'Healthcare & Life Sciences',
      challenge: 'Compliance requirements slowing development velocity',
      solution: 'Automated compliance pipeline with security-first architecture',
      timeline: '8 months',
      teamSize: '150+ engineers',
      beforeMetrics: {
        compliance: '6 weeks per audit',
        security: '12 vulnerabilities/month',
        delivery: '1 release per quarter',
        documentation: 'Manual processes'
      },
      afterMetrics: {
        compliance: '2 days per audit',
        security: '1 vulnerability/month',
        delivery: '2 releases per week',
        documentation: 'Automated generation'
      },
      keyResults: [
        '95% reduction in compliance audit time',
        '92% decrease in security vulnerabilities',
        '24x increase in release frequency',
        'Full HIPAA and SOC 2 compliance automation'
      ],
      testimonial: `SIGNA proved that security and velocity aren't mutually exclusive. We're now the fastest-moving compliant platform in our space.`,
      author: 'Dr. Amanda Foster, Chief Medical Officer',
      image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=800&h=600&fit=crop'
    }
  ];

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setActiveCase((prev) => (prev + 1) % caseStudies.length);
    }, 8000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, caseStudies.length]);

  const currentCase = caseStudies[activeCase];

  const handlePrevious = () => {
    setIsAutoPlaying(false);
    setActiveCase((prev) => (prev - 1 + caseStudies.length) % caseStudies.length);
  };

  const handleNext = () => {
    setIsAutoPlaying(false);
    setActiveCase((prev) => (prev + 1) % caseStudies.length);
  };

  return (
    <section className="py-20 bg-muted">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
            Featured Transformation Stories
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Real engineering transformations with measurable business impact
          </p>
        </motion.div>

        <div className="relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeCase}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-2xl overflow-hidden elevation-moderate"
            >
              <div className="grid lg:grid-cols-2 gap-0">
                {/* Content */}
                <div className="p-8 lg:p-12">
                  <div className="mb-6">
                    <div className="flex items-center space-x-2 text-sm text-text-secondary mb-2">
                      <span>{currentCase.industry}</span>
                      <span>•</span>
                      <span>{currentCase.teamSize}</span>
                      <span>•</span>
                      <span>{currentCase.timeline}</span>
                    </div>
                    <h3 className="text-2xl font-bold text-primary mb-2">{currentCase.company}</h3>
                    <p className="text-text-secondary">{currentCase.challenge}</p>
                  </div>

                  {/* Before/After Metrics */}
                  <div className="grid grid-cols-2 gap-6 mb-8">
                    <div>
                      <h4 className="text-sm font-semibold text-error mb-3 flex items-center">
                        <Icon name="TrendingDown" size={16} className="mr-2" />
                        Before SIGNA
                      </h4>
                      <div className="space-y-2">
                        {Object.entries(currentCase.beforeMetrics).map(([key, value]) => (
                          <div key={key} className="text-sm">
                            <span className="text-text-secondary capitalize">{key.replace(/([A-Z])/g, ' $1')}: </span>
                            <span className="font-medium text-primary">{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-success mb-3 flex items-center">
                        <Icon name="TrendingUp" size={16} className="mr-2" />
                        After SIGNA
                      </h4>
                      <div className="space-y-2">
                        {Object.entries(currentCase.afterMetrics).map(([key, value]) => (
                          <div key={key} className="text-sm">
                            <span className="text-text-secondary capitalize">{key.replace(/([A-Z])/g, ' $1')}: </span>
                            <span className="font-medium text-primary">{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Key Results */}
                  <div className="mb-8">
                    <h4 className="text-sm font-semibold text-primary mb-3">Key Results</h4>
                    <ul className="space-y-2">
                      {currentCase.keyResults.map((result, index) => (
                        <li key={index} className="flex items-start text-sm text-text-secondary">
                          <Icon name="Check" size={16} className="mr-3 mt-0.5 text-success flex-shrink-0" />
                          {result}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Testimonial */}
                  <div className="bg-muted rounded-lg p-6 mb-6">
                    <blockquote className="text-text-secondary italic mb-3">
                      "{currentCase.testimonial}"
                    </blockquote>
                    <cite className="text-sm font-medium text-primary">— {currentCase.author}</cite>
                  </div>

                  <Button
                    variant="default"
                    className="bg-cta hover:bg-cta/90 text-cta-foreground"
                    iconName="FileText"
                    iconPosition="right"
                  >
                    Read Full Case Study
                  </Button>
                </div>

                {/* Image */}
                <div className="relative h-64 lg:h-full">
                  <img
                    src={currentCase.image}
                    alt={`${currentCase.company} transformation`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent"></div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation Controls */}
          <div className="flex items-center justify-between mt-8">
            <div className="flex space-x-2">
              {caseStudies.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setIsAutoPlaying(false);
                    setActiveCase(index);
                  }}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === activeCase ? 'bg-accent' : 'bg-border hover:bg-accent/50'
                  }`}
                />
              ))}
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={handlePrevious}
                className="w-10 h-10 rounded-full bg-white border border-border hover:border-accent flex items-center justify-center transition-all duration-300"
              >
                <Icon name="ChevronLeft" size={20} className="text-text-secondary" />
              </button>
              <button
                onClick={handleNext}
                className="w-10 h-10 rounded-full bg-white border border-border hover:border-accent flex items-center justify-center transition-all duration-300"
              >
                <Icon name="ChevronRight" size={20} className="text-text-secondary" />
              </button>
              <button
                onClick={() => setIsAutoPlaying(!isAutoPlaying)}
                className="w-10 h-10 rounded-full bg-white border border-border hover:border-accent flex items-center justify-center transition-all duration-300 ml-2"
              >
                <Icon name={isAutoPlaying ? "Pause" : "Play"} size={16} className="text-text-secondary" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CaseStudyCarousel;